/**
 * カタログのリスト表示。
 *
 * 縦一列に並べ、左にサムネイル、右にメタデータを出す。
 * どの項目を出すかは利用者ごとの好み（確認したい属性が違う）なので、
 * ツールバーの「表示項目」から表示・非表示を切り替えられる。
 * 選択は localStorage に保存される（呼び出し側が持つ）。
 *
 * 項目を増やすときは LIST_FIELDS に足すだけでよい。
 * render が null を返すと、その素材ではラベルごと省かれる
 * （写真に「尺 —」を並べても情報量が無い。詳細モーダルと同じ方針）。
 */
import { useState, type ReactNode } from 'react'
import type { Asset, AssetField, Category, CategoryField } from '../lib/supabase.ts'
import { categoryPathLabel } from '../lib/categories.ts'
import { effectiveFields, formatFieldValue } from '../lib/fields.ts'
import {
  formatBytes,
  formatDate,
  formatDateTime,
  formatDuration,
  KIND_LABEL,
  STATUS_LABEL,
} from '../lib/format.ts'
import { AssetThumb } from './AssetThumb.tsx'

type FieldContext = {
  categoryName: (id: string | null) => string
  /** カスタム項目の「ラベル: 値」を連ねたもの。値が1つも無ければ null。 */
  attributeSummary: (a: Asset) => string | null
}

type FieldDef = {
  key: string
  label: string
  /** 既定で表示するか。 */
  def: boolean
  render: (a: Asset, ctx: FieldContext) => ReactNode
}

export const LIST_FIELDS: FieldDef[] = [
  { key: 'file_name', label: 'ファイル名', def: true, render: (a) => a.file_name ?? '—' },
  { key: 'kind', label: '種別', def: true, render: (a) => KIND_LABEL[a.kind] ?? a.kind },
  { key: 'status', label: '状態', def: true, render: (a) => STATUS_LABEL[a.status] ?? a.status },
  { key: 'byte_size', label: 'サイズ', def: true, render: (a) => formatBytes(a.byte_size) },
  {
    key: 'resolution',
    label: '解像度',
    def: true,
    render: (a) => (a.width && a.height ? `${a.width} × ${a.height}` : '—'),
  },
  {
    key: 'duration',
    label: '尺',
    def: true,
    render: (a) => (a.kind === 'video' ? formatDuration(a.duration_ms) : null),
  },
  {
    key: 'category',
    label: 'カテゴリ',
    def: true,
    render: (a, ctx) => ctx.categoryName(a.category_id),
  },
  {
    key: 'tags',
    label: 'タグ',
    def: true,
    render: (a) => (a.tags.length ? a.tags.join('、') : '—'),
  },
  {
    key: 'attributes',
    label: '項目',
    def: true,
    render: (a, ctx) => ctx.attributeSummary(a),
  },
  { key: 'captured_at', label: '撮影日時', def: false, render: (a) => formatDateTime(a.captured_at) },
  { key: 'published_at', label: '公開日', def: false, render: (a) => formatDate(a.published_at) },
  {
    key: 'sha1',
    label: 'SHA-1',
    def: false,
    render: (a) => (a.sha1 ? <span className="mono">{a.sha1}</span> : '—'),
  },
  { key: 'description', label: '説明', def: false, render: (a) => a.description || null },
]

export const defaultListFields = (): Record<string, boolean> =>
  Object.fromEntries(LIST_FIELDS.map((f) => [f.key, f.def]))

/**
 * 表示項目の設定。ツールバーに置く小さなポップオーバー。
 *
 * 「設定」タブはスタッフ専用で顧客には見えないため、リスト表示の項目設定は
 * カタログ側に置く（顧客も使う機能をスタッフ専用画面に置かない）。
 */
export function ListFieldSettings({
  visible,
  onChange,
}: {
  visible: Record<string, boolean>
  onChange: (next: Record<string, boolean>) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="fields-pop-wrap">
      <button
        type="button"
        className="fields-pop-button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M6.6 1.8a.7.7 0 0 1 .7-.6h1.4a.7.7 0 0 1 .7.6l.2 1.3c.44.16.85.4 1.22.68l1.24-.5a.7.7 0 0 1 .87.3l.7 1.22a.7.7 0 0 1-.17.9l-1.03.83a4.9 4.9 0 0 1 0 1.4l1.03.82a.7.7 0 0 1 .17.9l-.7 1.23a.7.7 0 0 1-.87.3l-1.24-.5a4.9 4.9 0 0 1-1.21.69l-.21 1.32a.7.7 0 0 1-.7.59H7.3a.7.7 0 0 1-.7-.59l-.2-1.32a4.9 4.9 0 0 1-1.22-.7l-1.24.5a.7.7 0 0 1-.87-.29l-.7-1.22a.7.7 0 0 1 .17-.9l1.03-.83a4.9 4.9 0 0 1 0-1.4l-1.03-.83a.7.7 0 0 1-.17-.9l.7-1.22a.7.7 0 0 1 .87-.3l1.24.5c.37-.29.78-.52 1.21-.69l.21-1.32zM8 10.1a2.1 2.1 0 1 0 0-4.2 2.1 2.1 0 0 0 0 4.2z" />
        </svg>
        表示項目
      </button>
      {open && (
        <>
          {/* クリックで閉じるための透明な背景。 */}
          <div className="pop-backdrop" onClick={() => setOpen(false)} />
          <div className="fields-pop" role="dialog" aria-label="リスト表示の項目設定">
            <p>リストに表示する項目</p>
            {LIST_FIELDS.map((f) => (
              <label key={f.key}>
                <input
                  type="checkbox"
                  checked={visible[f.key] ?? f.def}
                  onChange={(e) => onChange({ ...visible, [f.key]: e.target.checked })}
                />
                {f.label}
              </label>
            ))}
            <button
              type="button"
              className="link small"
              onClick={() => onChange(defaultListFields())}
            >
              既定に戻す
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export function AssetList({
  assets,
  categories,
  fields,
  assignments,
  visible,
  thumbs,
  broken,
  placeholder,
  onError,
  onOpen,
  extra,
}: {
  assets: Asset[]
  categories: Category[]
  /** カスタム項目の台帳と割り当て。「項目」列の表示に使う。 */
  fields: AssetField[]
  assignments: CategoryField[]
  /** 表示する項目（key → 表示するか）。未知のキーは既定値で補う。 */
  visible: Record<string, boolean>
  thumbs: Record<string, string>
  broken: Record<string, true>
  placeholder: (a: Asset) => ReactNode
  onError: (assetId: string) => void
  onOpen: (a: Asset) => void
  /** 行の下に足す表示（スタッフの変換状況など）。button の外に置かれる。 */
  extra?: (a: Asset) => ReactNode
}) {
  const ctx: FieldContext = {
    // 階層を「鉄道 › 新幹線 › 500系」の形で見せる（末端名だけだと別の枝の同名と区別できない）
    categoryName: (id) => (id ? categoryPathLabel(categories, id) : '未分類'),
    attributeSummary: (a) => {
      const pairs = effectiveFields(a.category_id, categories, fields, assignments)
        .map((f) => {
          const v = formatFieldValue(f, a.attributes?.[f.key])
          return v === null ? null : `${f.label}: ${v}`
        })
        .filter((x): x is string => x !== null)
      return pairs.length ? pairs.join('／') : null
    },
  }
  const columns = LIST_FIELDS.filter((f) => visible[f.key] ?? f.def)

  return (
    <ul className="rows">
      {assets.map((asset) => (
        <li key={asset.id}>
          <button type="button" className="lrow" onClick={() => onOpen(asset)}>
            <AssetThumb
              asset={asset}
              src={thumbs[asset.id] ?? null}
              broken={broken[asset.id]}
              placeholder={placeholder(asset)}
              onError={() => onError(asset.id)}
            >
              {asset.status !== 'published' && (
                <span className={`badge badge-${asset.status}`}>
                  {STATUS_LABEL[asset.status] ?? asset.status}
                </span>
              )}
            </AssetThumb>
            <div className="lrow-main">
              <h3>{asset.title}</h3>
              <dl className="lrow-fields">
                {columns.map((f) => {
                  const value = f.render(asset, ctx)
                  if (value == null) return null
                  return (
                    <div className="lfield" key={f.key}>
                      <dt>{f.label}</dt>
                      <dd>{value}</dd>
                    </div>
                  )
                })}
              </dl>
            </div>
          </button>
          {/* button の中に入れないこと（進捗文がアクセシブル名に混ざる）。 */}
          {extra?.(asset)}
        </li>
      ))}
    </ul>
  )
}
